/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { Dispatch, MutableRef, StateUpdater } from 'preact/hooks';
import type {
  Cell,
  CellContextMenuContext,
  CellOverride,
  SortCriterionDetail
} from '#UNSAFE_TableView';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type {
  ActiveCell,
  ActiveCellDetail,
  AppliedColumnWidths,
  CurrencyStatus,
  OverflowInfo,
  SizingInfo,
  StickyColumnInfo,
  TableColumn,
  ViewportInfo
} from '../Table';

import { getBoundaryColumnKey, getElementFromCell, isAutoFocusCell } from './TableDomUtils';
import { applyScrollDetail, resetVerticalScrollPosition } from './TableScrollUtils';
import { TableContextMenuResize, TableContextMenuSort } from '../TableContextMenu';

/**
 * Helper function to determine if a given active cell represents a 'transient' state.
 */
export function isTransientCell<K, C>(activeCell?: ActiveCell<K, C>) {
  return (
    activeCell != null &&
    (activeCell.type === 'loadMore' ||
      activeCell.type === 'loading' ||
      activeCell.type === 'pending')
  );
}

/**
 * Helper function to compare two given cell descriptors.
 */
export function cellComparator<K, C>(
  cell1?: ActiveCell<K, C> | Cell<K, C>,
  cell2?: ActiveCell<K, C> | Cell<K, C>
) {
  if (cell1 == null) {
    return cell2 == null;
  }
  if (cell2 == null) {
    return false;
  }
  if (cell1.type === cell2.type && cell1.rowKey === cell2.rowKey) {
    // special case the transient 'loading' cell types as column keys are hints used for when a user
    // navigates back to another region using the keyboard - otherwise we'd go to the first column
    if (cell1.type === 'loading' || cell1.type === 'loadMore' || cell1.type === 'pending') {
      return true;
    }
    return cell1.columnKey === cell2.columnKey;
  }
  return false;
}

export function getCellContextMenuContextFromCell<K extends string | number, D, C extends string>(
  tableData: CollectionData<K, D>[],
  columnsArray: TableColumn<K, D, C>[],
  setIsResizeDialogOpen: Dispatch<StateUpdater<boolean>>,
  cell?: ActiveCell<K, C>,
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[],
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void
) {
  if (cell?.type === 'data' || cell?.type === 'header' || cell?.type === 'footer') {
    let isSortable = false;
    let isResizable = false;
    for (let i = 0; i < columnsArray.length; i++) {
      const column = columnsArray[i];
      if (column.key === cell.columnKey) {
        isSortable = column.value.sortable === 'enabled';
        isResizable = column.value.resizable === 'enabled';
        break;
      }
    }
    let currentSortDirection;
    if (sortCriterion != null) {
      for (const criterion of sortCriterion) {
        if (criterion.key === cell.columnKey) {
          currentSortDirection = criterion.sortDirection;
          break;
        }
      }
    }
    const allMenuItems = {
      resizeColumn: (
        <TableContextMenuResize
          isDisabled={!isResizable}
          setIsResizeDialogOpen={setIsResizeDialogOpen}
        />
      ),
      sortColumn: (
        <TableContextMenuSort
          columnKey={cell.columnKey}
          isDisabled={!isSortable}
          currentSortDirection={currentSortDirection}
          onSortCriterionChange={onSortCriterionChange}
        />
      )
    };

    const hasResizableColumns = columnsArray.some((column: TableColumn<K, D, C>) => {
      return column.value.resizable === 'enabled';
    });
    const hasSortableColumns = columnsArray.some((column: TableColumn<K, D, C>) => {
      return column.value.sortable === 'enabled';
    });

    const defaultMenuItems =
      hasResizableColumns || hasSortableColumns ? (
        <>
          {hasResizableColumns ? allMenuItems['resizeColumn'] : undefined}
          {hasSortableColumns ? allMenuItems['sortColumn'] : undefined}
        </>
      ) : undefined;

    if (cell.type === 'data') {
      for (const rowData of tableData) {
        if (rowData.metadata.key === cell.rowKey) {
          return {
            allMenuItems,
            defaultMenuItems,
            rowData: rowData.data,
            rowKey: cell.rowKey,
            columnKey: cell.columnKey,
            type: 'data'
          } as CellContextMenuContext<K, D, C>;
        }
      }
    } else {
      return {
        allMenuItems,
        defaultMenuItems,
        columnKey: cell.columnKey,
        type: cell.type
      } as CellContextMenuContext<K, D, C>;
    }
  }
  return undefined;
}

/**
 * Helper function to determine the resulting cell from a cell override.
 */
function _getCellFromCellOverride<K, D, C>(
  tableData: CollectionData<K, D>[],
  columnsArray: TableColumn<K, D, C>[],
  cellOverride: CellOverride<K, C>,
  currentCell?: Cell<K, C>
): Cell<K, C> | undefined {
  let cellType;
  let cellRowKey;
  let cellColumnKey;
  if (cellOverride.rowKey != null) {
    cellType = 'data';
    cellRowKey = cellOverride.rowKey;
    if (cellOverride.columnKey != null) {
      cellColumnKey = cellOverride.columnKey;
    } else if (currentCell?.columnKey != null) {
      cellColumnKey = currentCell.columnKey;
    } else {
      cellColumnKey = getBoundaryColumnKey<K, D, C>(columnsArray, true);
    }
    if (cellColumnKey == null) {
      return undefined;
    }
  } else if (cellOverride.columnKey != null) {
    cellColumnKey = cellOverride.columnKey;
    if (cellOverride.type != null) {
      cellType = cellOverride.type;
    } else if (currentCell?.type != null && currentCell.type !== 'noData') {
      cellType = currentCell.type;
    } else {
      cellType = 'header';
    }
    if (cellType === 'data') {
      if (currentCell?.rowKey != null) {
        cellRowKey = currentCell.rowKey;
      } else if (tableData.length > 0) {
        cellRowKey = tableData[0].metadata.key;
      }
      if (cellRowKey == null) {
        return undefined;
      }
    }
  } else if (cellOverride.type != null) {
    cellType = cellOverride.type;
    if (cellType !== 'noData') {
      if (currentCell?.columnKey != null) {
        cellColumnKey = currentCell.columnKey;
      } else {
        cellColumnKey = getBoundaryColumnKey<K, D, C>(columnsArray, true);
      }
      if (cellColumnKey == null) {
        return undefined;
      }
      if (cellType === 'data') {
        if (currentCell?.rowKey != null) {
          cellRowKey = currentCell.rowKey;
        } else if (tableData.length > 0) {
          cellRowKey = tableData[0].metadata.key;
        }
        if (cellRowKey == null) {
          return undefined;
        }
      }
    }
  } else {
    return undefined;
  }
  return { type: cellType, rowKey: cellRowKey, columnKey: cellColumnKey } as Cell<K, C>;
}

/**
 * Helper function to determine the resulting cell type from a cell override.
 */
function _getCellTypeFromCellOverride<K, C>(
  cellOverride: CellOverride<K, C>,
  currentCell?: Cell<K, C>
) {
  if (cellOverride.type != null) {
    return cellOverride.type;
  }
  if (cellOverride.rowKey != null) {
    return 'data';
  }
  // if no type or rowKey was given, a columnKey must have been given
  if (currentCell?.type != null && currentCell.type !== 'noData') {
    return currentCell.type;
  }
  return 'header';
}

/**
 * Helper function to apply a current cell override if necessary.
 */
export function applyCurrentCellOverride<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  tableId: string,
  tableData: CollectionData<K, D>[],
  columnsArray: TableColumn<K, D, C>[],
  stickyColumnInfo: StickyColumnInfo,
  sizingInfo: SizingInfo,
  overflowInfo: OverflowInfo,
  currencyStatusRef: MutableRef<CurrencyStatus<K, D, C>>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K, C>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  isEmbeddedSelect: boolean,
  isPendingLayout: boolean,
  isDataLoaded: boolean,
  isRtl: boolean,
  currentCellOverride?: CellOverride<K, C>,
  appliedColumnWidths?: AppliedColumnWidths<C>
) {
  // when embedded select, use most recent non-null current cell value as override base
  const currentCell = isEmbeddedSelect
    ? currencyStatusRef.current.previousEmbeddedSelectCurrentCell
    : currencyStatusRef.current.currentCell;
  // ensure any new currentCellOverride settings are applied if necessary
  if (
    currencyStatusRef.current.hasPendingCurrentCellOverride ||
    currencyStatusRef.current.currentCellOverride !== currentCellOverride
  ) {
    if (currentCellOverride != null) {
      // attempt to find the cell the override specifies
      const realizedCell = _getCellFromCellOverride<K, D, C>(
        tableData,
        columnsArray,
        currentCellOverride,
        currentCell
      );
      if (realizedCell != null) {
        // override cell was determined - apply the override immediately
        onActiveCellChanged(
          { value: realizedCell },
          isAutoFocusCell(rootElement, tableId, realizedCell),
          false,
          true
        );
        currencyStatusRef.current.currentCellOverride = currentCellOverride;
        currencyStatusRef.current.hasPendingCurrentCellOverride = false;
        if (isEmbeddedSelect) {
          // scroll 'current-cell-override' vertically into view immediately for embedded select
          // otherwise, cell will be scrolled into view once the component or cell receives focus
          if (!isPendingLayout && getElementFromCell(rootElement, tableId, realizedCell) != null) {
            applyScrollDetail(
              rootElement,
              scrollerElement,
              viewportInfo,
              tableId,
              columnsArray,
              stickyColumnInfo,
              sizingInfo,
              overflowInfo,
              { cell: realizedCell, locationY: 'inView' },
              isRtl,
              appliedColumnWidths
            );
            currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
          } else if (isPendingLayout || !isDataLoaded) {
            // corresponding element was not found - data is not ready for scrolling
            currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = true;
          } else {
            // corresponding element was not found - value was garbage
            currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
          }
        } else {
          // not in an embedded select, so no scrolling should be performed
          currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
        }
      } else if (!isDataLoaded) {
        // override cell could not be determined - data is still fetching
        currencyStatusRef.current.hasPendingCurrentCellOverride = true;
      } else {
        // override cell could not be determined - value was garbage
        currencyStatusRef.current.currentCellOverride = currentCellOverride;
        currencyStatusRef.current.hasPendingCurrentCellOverride = false;
        currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
      }
    } else {
      // override is set to clear the current cell
      onActiveCellChanged({ value: currentCellOverride });
      currencyStatusRef.current.currentCellOverride = currentCellOverride;
      currencyStatusRef.current.hasPendingCurrentCellOverride = false;
      if (isEmbeddedSelect) {
        // scroll back to the top for embedded select
        resetVerticalScrollPosition(scrollerElement);
      }
      currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
    }
  } else if (currencyStatusRef.current.hasPendingCurrentCellOverrideScroll) {
    // try and apply any pending scroll from a previous cell override at this time
    if (currencyStatusRef.current.currentCell != null) {
      if (
        !isPendingLayout &&
        getElementFromCell(rootElement, tableId, currencyStatusRef.current.currentCell) != null
      ) {
        applyScrollDetail(
          rootElement,
          scrollerElement,
          viewportInfo,
          tableId,
          columnsArray,
          stickyColumnInfo,
          sizingInfo,
          overflowInfo,
          { cell: currencyStatusRef.current.currentCell, locationY: 'inView' },
          isRtl,
          appliedColumnWidths
        );
        currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
      } else if (!isPendingLayout && isDataLoaded) {
        // corresponding element was not found - value was garbage
        currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
      }
    } else {
      // there is no longer a current cell to try and scroll to
      currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
    }
  }
}

/**
 * Helper to determine if the given render state includes the cell specified.
 */
function _renderIncludesCell<K, D, C>(
  cell: ActiveCell<K, C>,
  columnsArray: TableColumn<K, D, C>[],
  tableData: CollectionData<K, D>[],
  hasMore: boolean,
  isPendingLayout: boolean,
  hasFooters: boolean
) {
  // pending layout is special as other regions exist, but are not yet navigable
  if (isPendingLayout) {
    return cell.type === 'pending';
  }
  if (cell.type === 'header' || (hasFooters && cell.type === 'footer')) {
    for (let i = 0; i < columnsArray.length; i++) {
      if (columnsArray[i].key === cell.columnKey) {
        return true;
      }
    }
  } else if (tableData.length > 0) {
    if (cell.type === 'loadMore') {
      return hasMore;
    }
    if (cell.type === 'data') {
      let isColumnValid = false;
      for (let i = 0; i < columnsArray.length; i++) {
        if (columnsArray[i].key === cell.columnKey) {
          isColumnValid = true;
          break;
        }
      }
      if (isColumnValid) {
        for (let i = 0; i < tableData.length; i++) {
          if (tableData[i].metadata.key === cell.rowKey) {
            return true;
          }
        }
      }
    }
  } else {
    return (cell.type === 'loading' && hasMore) || (cell.type === 'noData' && !hasMore);
  }
  return false;
}

/**
 * Helper to get the cell that should currently be a tab stop for the given render state.
 */
export function determineActiveCell<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  currencyStatusRef: MutableRef<CurrencyStatus<K, D, C>>,
  tableData: CollectionData<K, D>[],
  hasMore: boolean,
  isPendingLayout: boolean,
  isEmbeddedSelect: boolean,
  hasFooters: boolean,
  currentCellOverride?: CellOverride<K, C>
): ActiveCell<K, C> | undefined {
  if (isPendingLayout) {
    return { type: 'pending' };
  }
  // if no columns are specified, it is a special case 'noData' display
  const firstColumnKey = getBoundaryColumnKey<K, D, C>(columnsArray, true);
  if (firstColumnKey == null) {
    return { type: 'noData' };
  }
  // if nothing specifies where focus should go, use the first header cell
  const defaultFirstCell: ActiveCell<K, C> = {
    type: 'header',
    columnKey: firstColumnKey
  };

  // when embedded select, use most recent non-null current cell value as override base
  const currentCell = isEmbeddedSelect
    ? currencyStatusRef.current.previousEmbeddedSelectCurrentCell
    : currencyStatusRef.current.currentCell;
  if (
    currencyStatusRef.current.hasPendingCurrentCellOverride ||
    currencyStatusRef.current.currentCellOverride !== currentCellOverride
  ) {
    if (currentCellOverride != null) {
      // attempt to find the cell the override specifies
      const realizedCell = _getCellFromCellOverride<K, D, C>(
        tableData,
        columnsArray,
        currentCellOverride,
        currentCell
      );
      if (
        realizedCell != null &&
        _renderIncludesCell(
          realizedCell,
          columnsArray,
          tableData,
          hasMore,
          isPendingLayout,
          hasFooters
        )
      ) {
        return realizedCell;
      }
      // if here, the resulting cell from the override could not be determined or was not valid
      const overrideCellType = _getCellTypeFromCellOverride(currentCellOverride, currentCell);
      if (overrideCellType === 'data') {
        if (tableData.length === 0 && hasMore) {
          // pending 'data' override when 'loading' skeletons are shown
          return { type: 'loading', columnKey: currentCell?.columnKey as C };
        }
      } else if (overrideCellType === 'noData' && tableData.length === 0 && hasMore) {
        // pending 'noData' override when 'loading' skeletons are shown
        return { type: 'loading', columnKey: currentCell?.columnKey as C };
      }
    } else if (
      _renderIncludesCell(
        defaultFirstCell,
        columnsArray,
        tableData,
        hasMore,
        isPendingLayout,
        hasFooters
      )
    ) {
      return defaultFirstCell;
    } else {
      return undefined;
    }
  }

  // if here, there is no current cell override that can be applied during this render
  const activeCell = currencyStatusRef.current.activeCell;
  if (activeCell != null) {
    if (activeCell.type === 'loadMore') {
      const initialData = currencyStatusRef.current.loadMoreInitialData;
      // if loadMore skeletons are no longer shown, or the data has changed, shift to
      // the next row in the data set if possible - otherwise just the closest row
      if (tableData.length > 0 && tableData !== initialData) {
        const rowKey =
          initialData != null && tableData.length > initialData.length
            ? tableData[initialData.length].metadata.key
            : tableData[tableData.length - 1].metadata.key;
        const columnKey = currentCell?.columnKey != null ? currentCell.columnKey : firstColumnKey;
        const cell: ActiveCell<K, C> = { type: 'data', rowKey: rowKey, columnKey: columnKey };
        if (
          _renderIncludesCell(cell, columnsArray, tableData, hasMore, isPendingLayout, hasFooters)
        ) {
          return cell;
        }
      } else if (tableData.length === 0) {
        if (hasMore) {
          return { type: 'loading', columnKey: currentCell?.columnKey as C };
        } else {
          return { type: 'noData' };
        }
      }
    }
    if (
      _renderIncludesCell(activeCell, columnsArray, tableData, hasMore, isPendingLayout, hasFooters)
    ) {
      return activeCell;
    } else if (activeCell.type === 'loading') {
      if (currentCell != null && (currentCell.type === 'data' || currentCell.type === 'noData')) {
        // if the current cell is already set to 'data' or 'noData', and is now available, return it
        if (
          _renderIncludesCell(
            currentCell,
            columnsArray,
            tableData,
            hasMore,
            isPendingLayout,
            hasFooters
          )
        ) {
          return currentCell;
        }
      }
      // otherwise, find the first row if available, and try and maintain the columnKey
      if (tableData.length > 0) {
        const rowKey = tableData[0].metadata.key;
        const columnKey = currentCell?.columnKey != null ? currentCell.columnKey : firstColumnKey;
        const cell: ActiveCell<K, C> = { type: 'data', rowKey: rowKey, columnKey: columnKey };
        if (
          _renderIncludesCell(cell, columnsArray, tableData, hasMore, isPendingLayout, hasFooters)
        ) {
          return cell;
        }
      } else {
        return { type: 'noData' };
      }
    }
  }

  // if here, there is no active cell, or the active cell is no longer valid
  if (currentCell != null) {
    if (
      _renderIncludesCell(
        currentCell,
        columnsArray,
        tableData,
        hasMore,
        isPendingLayout,
        hasFooters
      )
    ) {
      return currentCell;
    }
    if (currentCell.type === 'data') {
      if (tableData.length === 0 && hasMore) {
        // pending 'data' override when 'loading' skeletons are shown
        return { type: 'loading', columnKey: currentCell.columnKey };
      }
    } else if (currentCell.type === 'noData' && tableData.length === 0 && hasMore) {
      // pending 'noData' override when 'loading' skeletons are shown
      return { type: 'loading', columnKey: firstColumnKey };
    }
  }
  if (
    _renderIncludesCell(
      defaultFirstCell,
      columnsArray,
      tableData,
      hasMore,
      isPendingLayout,
      hasFooters
    )
  ) {
    return defaultFirstCell;
  }
  return undefined;
}
